import { ViewClinicalSpecialistModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ViewClinicalSpecialistModal';
import { RemoveResourceModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/RemoveResourceModal';
import { QuoteDetailsModalProps } from '@lp/features/RoutePlanner/components/QuoteDetails/QuoteDetailsModal';
import { ViewDeviceModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ViewDeviceModal';
import { ViewSurgeonModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ViewSurgeonModal';
import { ViewHubModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ViewHubModal';
import { RoutePlannerClinicalSpecialistListProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ClinicalSpecialistListTable/ClinicalSpecialistList';
import { RoutePlannerDeviceListProps } from '@lp/features/RoutePlanner/components/RouteModals/components/DeviceListTable/DeviceList';
import { RoutePlannerSurgeonListProps } from '@lp/features/RoutePlanner/components/RouteModals/components/SurgeonListTable/SurgeonList';
import { RoutePlannerHubsListProps } from '@lp/features/RoutePlanner/components/RouteModals/components/HubsListTable/HubList';
import { RemoveClinicalSpecialistModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/RemoveClinicalSpecialistModal';
import { RemoveDeviceModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/RemoveDeviceModal';
import { ViewRouteSchemaModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ViewRouteSchemaModal';
import { OverrideFlowModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/OverrideFlowModal';
import { QuoteCommentModalProps } from '@lp/features/RoutePlanner/components/QuoteComment/QuoteCommentModal';
import { ConfirmReservationModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ConfirmReservationModal';
import { RemoveConnectedLegsModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/RemoveConnectedLegsModal';
import {
  CreateRouteLegProps,
  EditRouteLegProps,
  LegFormWrapperProps,
} from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { RemoveActiveGroundTripLegModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/RemoveActiveGroundTripLegModal';
import { DeviceDeliveryInfoModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/DeviceDeliveryInfoModal';
import { ReplaceExistingRouteModalProps } from '@lp/features/RoutePlanner/components/RouteModals/components/ReplaceExistingRouteModal';

export enum RouteModalTypes {
  AddNewLeg = 'add-new-leg',
  EditLeg = 'edit-leg',
  AssignSpecialist = 'assign-specialist',
  ViewSpecialist = 'view-specialist',
  AssignDevice = 'assign-device',
  ViewDevice = 'view-device',
  AssignSurgeon = 'assign-surgeon',
  ViewSurgeon = 'view-surgeon',
  AssignHub = 'assign-hub',
  ViewHub = 'view-hub',
  RemoveResource = 'remove-resource',
  RemoveCS = 'remove-cs',
  RemoveDevice = 'remove-device',
  RouteSchema = 'route-schema',
  LogisticsStatusLog = 'logistics-status-log',
  QuoteDetails = 'quote-details',
  QuoteComment = 'quote-comment',
  QuoteRecall = 'quote-recall',
  OverrideFlow = 'override-flow',
  ConfirmReservation = 'confirm-reservation',
  SecondaryResource = 'secondary-resource',
  RemoveConnectedLegs = 'remove-connected-legs',
  DeviceDeliveryInfo = 'device-delivery-info',
  TrialRandomizationResult = 'trial-randomization-result',
  RemoveActiveGroundTripLeg = 'remove-active-ground-trip-leg',
  ReplaceExistingRoute = 'replace-existing-route',
}

export type EmptyModalContext = {
  type: null;
  context: null;
};

export type LogisticsStatusLogModalContext = {
  type: RouteModalTypes.LogisticsStatusLog;
  context: null;
};

export type QuoteDetailsModalContext = {
  type: RouteModalTypes.QuoteDetails;
  context: Pick<
    QuoteDetailsModalProps,
    'onApproveTxCenterProvider' | 'onApprove' | 'onReject' | 'onRecall' | 'recallQuote' | 'isViewOnly' | 'desyncedState'
  >;
};

export type QuoteCommentModalContext = {
  type: RouteModalTypes.QuoteComment;
  context: QuoteCommentModalProps & { title: string };
};

export type AssignSpecialistModalContext = {
  type: RouteModalTypes.AssignSpecialist;
  context: RoutePlannerClinicalSpecialistListProps;
};

export type ViewSpecialistModalContext = {
  type: RouteModalTypes.ViewSpecialist;
  context: Pick<
    ViewClinicalSpecialistModalProps,
    'isAssignEnabled' | 'isRemoveEnabled' | 'resource' | 'onAssign' | 'onRemove'
  >;
};

export type RemoveResourceModalContext = {
  type: RouteModalTypes.RemoveResource;
  context: Pick<RemoveResourceModalProps, 'resourceId' | 'onRemove'>;
};

export type AssignDeviceModalContext = {
  type: RouteModalTypes.AssignDevice;
  context: RoutePlannerDeviceListProps;
};

export type ViewDeviceModalContext = {
  type: RouteModalTypes.ViewDevice;
  context: Pick<
    ViewDeviceModalProps,
    'isAssignEnabled' | 'isRemoveEnabled' | 'resource' | 'organType' | 'onAssign' | 'onRemove'
  >;
};

export type AssignSurgeonModalContext = {
  type: RouteModalTypes.AssignSurgeon;
  context: RoutePlannerSurgeonListProps;
};

export type ViewSurgeonModalContext = {
  type: RouteModalTypes.ViewSurgeon;
  context: Pick<ViewSurgeonModalProps, 'isAssignEnabled' | 'isRemoveEnabled' | 'resource' | 'onAssign' | 'onRemove'>;
};

export type AssignHubModalContext = {
  type: RouteModalTypes.AssignHub;
  context: RoutePlannerHubsListProps;
};

export type ViewHubModalContext = {
  type: RouteModalTypes.ViewHub;
  context: Pick<ViewHubModalProps, 'hubId' | 'onRemove'>;
};

export type AddNewLegModalContext = {
  type: RouteModalTypes.AddNewLeg;
  context: Omit<CreateRouteLegProps, 'mode' | 'onClose'> & Pick<LegFormWrapperProps, 'onSubmit'>;
};

export type EditLegModalContext = {
  type: RouteModalTypes.EditLeg;
  context: Omit<EditRouteLegProps, 'mode' | 'onClose'> & Pick<LegFormWrapperProps, 'onSubmit'>;
};

export type RemoveCSModalContext = {
  type: RouteModalTypes.RemoveCS;
  context: Pick<RemoveClinicalSpecialistModalProps, 'organType' | 'resource' | 'onConfirmation' | 'onAction'>;
};

export type RemoveDeviceModalContext = {
  type: RouteModalTypes.RemoveDevice;
  context: Pick<RemoveDeviceModalProps, 'organType' | 'resource' | 'onRemove'>;
};

export type RouteSchemaModalContext = {
  type: RouteModalTypes.RouteSchema;
  context: Pick<ViewRouteSchemaModalProps, 'route'>;
};

export type OverrideFlowModalContext = {
  type: RouteModalTypes.OverrideFlow;
  context: Pick<OverrideFlowModalProps, 'title' | 'description' | 'actionButtonTitle' | 'onAction'>;
};

export type ConfirmReservationModalContext = {
  type: RouteModalTypes.ConfirmReservation;
  context: Pick<ConfirmReservationModalProps, 'title' | 'description' | 'onSelect'>;
};

export type SecondaryResourceModalContext = {
  type: RouteModalTypes.SecondaryResource;
  context: null;
};

export type RemoveConnectedLegsModalContext = {
  type: RouteModalTypes.RemoveConnectedLegs;
  context: Pick<RemoveConnectedLegsModalProps, 'legs' | 'onRemove'>;
};

export type DeviceDeliveryInfoModalContext = {
  type: RouteModalTypes.DeviceDeliveryInfo;
  context: Pick<DeviceDeliveryInfoModalProps, 'message'>;
};

export type QuoteRecallModalContext = {
  type: RouteModalTypes.QuoteRecall;
  context: {
    onRecall: (caseId: string, comment: string) => Promise<void>;
  };
};

export type TrialRandomizationResultModalContext = {
  type: RouteModalTypes.TrialRandomizationResult;
  context: null;
};

export type RemoveActiveGroundTripModalContext = {
  type: RouteModalTypes.RemoveActiveGroundTripLeg;
  context: Pick<RemoveActiveGroundTripLegModalProps, 'legId' | 'tripNumber' | 'tripStatus' | 'onRemove'>;
};

export type ReplaceExistingRouteModalContext = {
  type: RouteModalTypes.ReplaceExistingRoute;
  context: Pick<ReplaceExistingRouteModalProps, 'isPartialRoute' | 'startTime' | 'onConfirm'>;
};

export type ModalContextType =
  | EmptyModalContext
  | QuoteDetailsModalContext
  | QuoteCommentModalContext
  | LogisticsStatusLogModalContext
  | AssignSpecialistModalContext
  | ViewSpecialistModalContext
  | AssignDeviceModalContext
  | ViewDeviceModalContext
  | AssignSurgeonModalContext
  | ViewSurgeonModalContext
  | AssignHubModalContext
  | ViewHubModalContext
  | AddNewLegModalContext
  | EditLegModalContext
  | RemoveResourceModalContext
  | RemoveCSModalContext
  | RemoveDeviceModalContext
  | RouteSchemaModalContext
  | ConfirmReservationModalContext
  | OverrideFlowModalContext
  | SecondaryResourceModalContext
  | RemoveConnectedLegsModalContext
  | DeviceDeliveryInfoModalContext
  | TrialRandomizationResultModalContext
  | RemoveActiveGroundTripModalContext
  | ReplaceExistingRouteModalContext
  | QuoteRecallModalContext;
